/**
 * TypeScript types for ICF Progress Tracking
 * 
 * Defines interfaces for:
 * - ICF competency data
 * - Progress tracking responses
 * - Session analysis data
 * - API request/response schemas
 */

// Enums

export enum ICFCompetency {
  DEMONSTRATES_ETHICAL_PRACTICE = 'demonstrates_ethical_practice',
  EMBODIES_COACHING_MINDSET = 'embodies_coaching_mindset',
  ESTABLISHES_MAINTAINS_AGREEMENTS = 'establishes_maintains_agreements',
  CULTIVATES_TRUST_SAFETY = 'cultivates_trust_safety',
  MAINTAINS_PRESENCE = 'maintains_presence',
  LISTENS_ACTIVELY = 'listens_actively',
  EVOKES_AWARENESS = 'evokes_awareness',
  FACILITATES_CLIENT_GROWTH = 'facilitates_client_growth'
}

export enum AnalysisStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  EXCLUDED = 'excluded'
}

// Base Types

export interface CompetencyScores {
  demonstrates_ethical_practice: number;
  embodies_coaching_mindset: number;
  establishes_maintains_agreements: number;
  cultivates_trust_safety: number;
  maintains_presence: number;
  listens_actively: number;
  evokes_awareness: number;
  facilitates_client_growth: number;
}

export interface CompetencyScorePayloadEntry {
  score?: number;
  level?: string;
  level_ordinal?: number;
  positive_examples?: string[];
  growth_examples?: string[];
}

export type CompetencyScorePayload = Record<string, CompetencyScorePayloadEntry>;

export interface SessionInsights {
  key_observations: string[];
  coaching_techniques_used: string[];
  areas_for_improvement: string[];
  positive_highlights: string[];
  specific_recommendations: string[];
}

export interface AIInsights {
  overall_assessment?: string;
  key_strengths?: string[];
  improvement_areas?: string[];
  recommendations?: string[];
  competency_highlights?: Record<string, string>;
  trend_analysis?: string;
  next_steps?: string[];
  confidence_score?: number;
}

// Request Schemas

export interface TrackingPreferencesRequest {
  is_tracking_enabled?: boolean;
  include_future_sessions?: boolean;
  data_retention_days?: number;
}

export interface ICFGoalUpdateRequest {
  target_level?: 'acc' | 'pcc' | 'mcc' | null;
  target_date?: string | null;
}

export interface ICFProgressUpdateRequest {
  competency_scores: Partial<CompetencyScores>;
}

// Response Schemas

export interface ICFProgressResponse {
  id: number;
  user_id: number;
  school_id?: number;
  competency_scores?: CompetencyScores;
  overall_score?: number;
  total_sessions_analyzed: number;
  last_analysis_date?: string;
  is_tracking_enabled: boolean;
  include_future_sessions: boolean;
  data_retention_days: number;
  created_at: string;
  updated_at: string;
  last_accessed_at?: string;
  
  // Optional encrypted fields (only included with proper permissions)
  strengths?: string[];
  development_areas?: string[];
}

export interface SessionAnalysisResponse {
  id: number;
  session_id: number;
  user_id: number;
  school_id?: number;
  session_title?: string;
  progress_tracking_id: number;
  has_progress_tracking?: boolean;
  include_in_analysis: boolean;
  analysis_status: AnalysisStatus;
  last_analyzed_at?: string;
  coaching_quality_score?: number;
  ai_model_version?: string;
  analysis_duration_ms?: number;
  message_count_analyzed?: number;
  created_at: string;
  updated_at: string;
  
  // Optional encrypted fields
  competency_scores?: CompetencyScores | CompetencyScorePayload;
  session_insights?: SessionInsights;
}

export interface CompetencyHistoryResponse {
  id: number;
  progress_tracking_id: number;
  user_id: number;
  school_id?: number;
  competency_name: ICFCompetency;
  score: number;
  previous_score?: number;
  change_amount: number;
  sessions_analyzed_count: number;
  trigger_session_id?: number;
  change_reason?: string;
  recorded_at: string;
  
  // Optional encrypted fields
  improvement_notes?: string;
  regression_analysis?: string;
}

export interface ProgressDashboardResponse {
  progress: ICFProgressResponse;
  recent_analyses: SessionAnalysisResponse[];
  competency_trends: CompetencyHistoryResponse[];
  insights: AIInsights;
  strengths: string[];
  development_areas: string[];
  competency_progress?: Record<string, CompetencyProgressMetrics>;
  overall_progress?: OverallProgressMetrics;
  readiness?: ICFReadinessSnapshot;
  level_up?: LevelUpCelebration;
  development_plan?: DevelopmentPlanArtifact;
}

export interface ProgressTeaserInsight {
  competency_key?: string;
  competency_code?: string;
  competency_level?: string;
  summary: string;
}

export interface ProgressTeaserResponse {
  locked: true;
  tracked_session_count: number;
  estimated_level?: string;
  estimated_level_key?: string;
  insight?: ProgressTeaserInsight;
  practice_plan: PracticePlanItem[];
  locked_practice_plan_count: number;
  locked_sections: string[];
  development_plan?: DevelopmentPlanArtifact;
}

export interface DevelopmentPlanFocusArea {
  competency?: string;
  title: string;
  why: string;
}

export interface DevelopmentPlanPracticeItem {
  competency?: string;
  title: string;
  body: string;
  success_signal: string;
}

export interface DevelopmentPlanArtifact {
  focus_areas: DevelopmentPlanFocusArea[];
  practice_items: DevelopmentPlanPracticeItem[];
  source: string;
  updated_at: string;
  version: number;
  /** Onboarding plans only. Mentor plans (`mentor_plan`) also carry their text and the "Start here" step. */
  plan_kind?: 'development_plan' | 'mentor_workspace' | 'mentor_plan' | null;
  headline?: string | null;
  summary?: string | null;
  first_action?: string | null;
  weekly_practice?: string | null;
  next_milestone?: string | null;
  start_here_step?: string | null;
}

export interface LevelUpCelebration {
  previous_level: string;
  new_level: string;
  occurred_at: string;
}

export type ProgressDashboardPayload = ProgressDashboardResponse | ProgressTeaserResponse;

export function isProgressTeaserResponse(
  payload: ProgressDashboardPayload | null | undefined,
): payload is ProgressTeaserResponse {
  return Boolean(payload && 'locked' in payload && payload.locked === true);
}

export interface ConsistencyStats {
  percentage: number;
  numerator: number;
  denominator: number;
}

export interface MomentumStats {
  direction: 'up' | 'down' | 'flat' | null;
  delta_levels: number;
}

export interface CompetencyTimelinePoint {
  timestamp: string;
  level?: string;
  level_ordinal?: number;
  session_id?: number;
}

export interface CompetencyProgressMetrics {
  current_level?: string;
  current_level_ordinal?: number;
  consistency: ConsistencyStats;
  momentum: MomentumStats;
  timeline: CompetencyTimelinePoint[];
}

export interface OverallProgressMetrics {
  timeline: CompetencyTimelinePoint[];
  consistency: ConsistencyStats;
  momentum: MomentumStats;
}

export interface TrendState {
  status: 'locked' | 'sparse' | 'ready' | string;
  sessions_required: number;
  sessions_until_unlock: number;
  message: string;
}

export interface ReadinessEvidenceRef {
  session_id?: number;
  timestamp?: string;
  quote: string;
  rationale: string;
  competency_key?: string;
  competency_code?: string;
}

export interface ReadinessCompetencyLane {
  competency_key: string;
  competency_code?: string;
  status: 'needs_review' | 'demonstrated' | 'stable_strength' | 'emerging_strength' | 'inconsistent' | 'target_blocker' | 'insufficient_evidence' | string;
  current_level?: string;
  current_level_key?: string;
  current_level_ordinal?: number;
  target_level?: string;
  target_level_key?: string;
  target_gap?: number;
  consistency: Record<string, any>;
  momentum: Record<string, any>;
  evidence_counts: Record<string, number>;
  latest_evidence: ReadinessEvidenceRef[];
  /** C1 only: the ethics veto fired in a window session (a flag, never a level). */
  ethics_concern?: boolean;
}

export interface CompetencyDetailHistoryItem {
  session_id?: number;
  session_title?: string;
  analyzed_at?: string;
  level_key: string;
  consistency: ConsistencyStats;
}

export interface CompetencyDetailEvidenceItem {
  session_id?: number;
  session_title?: string;
  quote: string;
  level_key?: string;
  polarity: 'strength' | 'development';
  analyzed_at?: string;
}

export interface CompetencyPracticeSuggestion {
  title: string;
  body: string;
  success_signal: string;
}

export interface CompetencyDetailResponse {
  competency: ICFCompetency;
  lane: ReadinessCompetencyLane;
  session_history: CompetencyDetailHistoryItem[];
  evidence: CompetencyDetailEvidenceItem[];
  practice_suggestions: CompetencyPracticeSuggestion[];
  level_criteria: Record<'acc' | 'pcc' | 'mcc', string[]>;
  trend_state: TrendState;
}

export interface PracticeExamQuestion {
  id: string;
  competency: string;
  option_count: number;
}

export interface PracticeExamQuestionsResponse {
  questions: PracticeExamQuestion[];
}

export interface PracticeExamAnswer {
  question_id: string;
  answer: number;
}

export interface PracticeExamResultItem {
  question_id: string;
  competency: string;
  correct: boolean;
  selected_answer: number;
  correct_answer: number;
}

export interface PracticeExamCompetencyScore {
  correct: number;
  total: number;
  percentage: number;
}

export interface PracticeExamAttemptResponse {
  correct: number;
  total: number;
  percentage: number;
  competency_scores: Record<string, PracticeExamCompetencyScore>;
  results: PracticeExamResultItem[];
}

export interface ReadinessPattern {
  pattern_type: 'strength' | 'development' | string;
  competency_key: string;
  competency_code?: string;
  title: string;
  description: string;
  seen_count: number;
  last_seen_session_id?: number;
  trend: string;
  evidence: ReadinessEvidenceRef[];
}

export interface PracticePlanItem {
  priority: number;
  competency_key: string;
  competency_code?: string;
  title: string;
  rationale: string;
  try_in_next_session: string[];
  success_signal: string;
}

export interface PracticePlanCheckin {
  priority: number;
  competency_key: string;
  competency_code?: string;
  title: string;
  success_signal: string;
  status: 'met' | 'partial' | 'missed' | 'not_observed' | string;
  summary: string;
  evidence: ReadinessEvidenceRef[];
  next_try: string[];
}

export interface EvidencePortfolio {
  strength_examples: ReadinessEvidenceRef[];
  development_examples: ReadinessEvidenceRef[];
}

export interface SemanticMemorySummary {
  matches_considered: number;
  themes: Record<string, any>[];
}

export interface AnalysisWindowSummary {
  sessions_considered: number;
  available_sessions: number;
  window_size: number;
}

export interface ICFReadinessSnapshot {
  target_level?: string;
  target_level_key?: string;
  target_is_explicit?: boolean;
  target_date?: string;
  days_until_target?: number;
  estimated_level?: string;
  estimated_level_key?: string;
  competencies_at_target?: number;
  total_competencies?: number;
  estimated_sessions_to_target?: number;
  goal_summary?: string;
  confidence: 'low' | 'medium' | 'high' | string;
  readiness_status: 'no_data' | 'needs_more_evidence' | 'developing' | 'near_target' | 'target_supported' | string;
  summary: string;
  disclaimer: string;
  trend_state?: TrendState;
  analysis_window: AnalysisWindowSummary;
  level_distribution: Record<string, number>;
  overall_progress?: OverallProgressMetrics;
  competency_lanes: ReadinessCompetencyLane[];
  recurring_patterns: ReadinessPattern[];
  practice_plan: PracticePlanItem[];
  practice_checkins?: PracticePlanCheckin[];
  evidence_portfolio: EvidencePortfolio;
  semantic_memory: SemanticMemorySummary;
  /**
   * CRF-shaped readiness (spec §1.3–1.5, WS-7). Present only when the coach has
   * a goal level and at least one analysis ran the official instrument at it.
   */
  crf_readiness?: CrfReadiness;
}

/** Union-rule status of a single official form statement across the coach's sessions. */
export interface CrfStatementStatus {
  demonstrated: boolean;
  /** Session numbers where the statement was rated Meets/Exceeds (or Observed for PCC). */
  sessions_demonstrated: number[];
  /** Session numbers where the statement was rated N/A (never counts as demonstrated). */
  sessions_na: number[];
}

/** Per-competency CRF gate (spec §1.4 — integer thresholds, never a percentage). */
export interface CrfReadinessCompetency {
  code: 'C3' | 'C4' | 'C5' | 'C6' | 'C7' | 'C8';
  demonstrated: number;
  required: number;
  total: number;
  short: number;
  met: boolean;
  missing_statement_ids: string[];
}

/** One competency-scoped practice item derived from the unmet gates. */
export interface CrfGapPlanItem {
  competency_code: string;
  statements_short: number;
  statement_ids: string[];
  practice: string;
}

export interface CrfReadiness {
  /** The coach's goal level; the instrument the statements come from. */
  level: 'acc' | 'pcc' | 'mcc';
  form_revision: string;
  sessions_considered: number;
  sessions_not_assessed: number;
  /** Optional: ids of included sessions with no instrument at this level (re-run CTA). */
  sessions_not_assessed_ids?: number[];
  verdict: 'demonstrated' | 'not_yet';
  statements_short_total: number;
  per_competency: CrfReadinessCompetency[];
  statements: Record<string, CrfStatementStatus>;
  gap_plan: CrfGapPlanItem[];
}

export interface ICFGoalResponse {
  target_level?: string | null;
  target_level_key?: string | null;
  target_date?: string | null;
  target_is_explicit: boolean;
}

// Extended Response Types

export interface ProgressInsightsResponse {
  overall_assessment: string;
  key_strengths: string[];
  improvement_areas: string[];
  recommendations: string[];
  competency_highlights: Record<string, string>;
  trend_analysis: string;
  next_steps: string[];
  confidence_score: number;
}

export interface CompetencyTrendResponse {
  competency_name: ICFCompetency;
  current_score: number;
  previous_score: number;
  change_percentage: number;
  trend_direction: 'improving' | 'declining' | 'stable';
  trend_strength: 'strong' | 'moderate' | 'weak';
  data_points: {
    date: string;
    score: number;
    change_from_previous?: number;
  }[];
  insights: string;
}

export interface SessionAnalysisInsightsResponse {
  session_id: number;
  overall_quality_score: number;
  competency_breakdown: CompetencyScores;
  key_observations: string[];
  coaching_techniques_used: string[];
  areas_for_improvement: string[];
  positive_highlights: string[];
  specific_recommendations: string[];
  analysis_confidence: number;
}

export interface BulkAnalysisStatusResponse {
  total_sessions: number;
  processed_sessions: number;
  failed_sessions: number;
  pending_sessions: number;
  start_time: string;
  estimated_completion?: string;
  status: 'running' | 'completed' | 'failed' | 'cancelled';
  progress_percentage: number;
  current_session_id?: number;
}

export interface DataExportResponse {
  export_id: string;
  file_url?: string;
  status: 'processing' | 'completed' | 'failed';
  created_at: string;
  expires_at: string;
  file_size_bytes?: number;
  record_count: number;
  export_type: 'full' | 'competency_specific' | 'date_range';
}

export interface PrivacyComplianceResponse {
  operation_type: 'data_export' | 'data_purge' | 'anonymization';
  status: 'completed' | 'failed';
  affected_records: number;
  completion_time: string;
  compliance_notes: string;
}

// Utility Types

export interface CompetencyInfo {
  key: ICFCompetency;
  name: string;
  description: string;
  color: string;
  weight?: number;
}

export interface ChartDataPoint {
  x: string | number;
  y: number;
  label?: string;
  color?: string;
}

export interface ProgressMetrics {
  overall_score: number;
  total_sessions: number;
  analysis_date?: string;
  tracking_enabled: boolean;
  competency_averages: CompetencyScores;
  improvement_rate?: number;
  consistency_score?: number;
}

export interface SessionMetrics {
  session_id: number;
  quality_score?: number;
  analysis_status: AnalysisStatus;
  message_count?: number;
  analysis_duration?: number;
  included_in_progress: boolean;
  competency_performance?: CompetencyScores;
}

// Filter and Pagination Types

export interface SessionFilter {
  status?: AnalysisStatus[];
  include_excluded?: boolean;
  date_range?: {
    start: string;
    end: string;
  };
  quality_score_range?: {
    min: number;
    max: number;
  };
}

export interface ProgressFilter {
  competencies?: ICFCompetency[];
  date_range?: {
    start: string;
    end: string;
  };
  score_range?: {
    min: number;
    max: number;
  };
}

export interface PaginationParams {
  limit?: number;
  offset?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

// Error Types

export interface ICFProgressError {
  code: string;
  message: string;
  details?: Record<string, any>;
  user_message?: string;
}

export interface ValidationError {
  field: string;
  message: string;
  code: string;
  value?: any;
}

// Configuration Types

export interface ICFCompetencyConfig {
  competencies: CompetencyInfo[];
  scoring: {
    min_score: number;
    max_score: number;
    score_levels: {
      min: number;
      max: number;
      label: string;
      color: string;
    }[];
  };
  analysis: {
    min_sessions_required: number;
    confidence_threshold: number;
    update_frequency_days: number;
  };
}

// ---------------------------------------------------------------------------
// Structured Analysis Types (Phase3Result)
// ---------------------------------------------------------------------------

export interface CompetencyEvidence {
  timestamp: string;
  quote: string;
  rationale: string;
  marker_ids: string[];
}

export interface CompetencyAnalysis {
  competency_code: string;
  level: string;
  positive_examples: CompetencyEvidence[];
  development_examples: CompetencyEvidence[];
  additional_evidence: CompetencyEvidence[];
  narrative: string;
}

export interface AnalysisStructured {
  overall_assessment: string;
  overall_level: string;
  what_who_balance: string;
  strengths: CompetencyEvidence[];
  development_areas: CompetencyEvidence[];
  competency_analyses: Record<string, CompetencyAnalysis>;
  recommendations: string[];
}

// ---------------------------------------------------------------------------
// Evidence Explorer Types
// ---------------------------------------------------------------------------

export type EvidenceType = 'positive' | 'development' | 'additional' | 'strength' | 'development_area';

export interface EvidenceItem {
  competency: string;
  evidence_type: EvidenceType;
  timestamp: string;
  quote: string;
  rationale: string;
  marker_ids: string[];
}

export interface EvidenceFilterParams {
  competency?: string;
  marker_id?: string;
  evidence_type?: string;
  page?: number;
  page_size?: number;
}

export interface EvidenceResponse {
  items: EvidenceItem[];
  total: number;
  page: number;
  page_size: number;
  has_structured_data: boolean;
  overall_level?: string;
  what_who_balance?: string;
}

// ---------------------------------------------------------------------------
// Actions
